/**
 * m2m-checkin のキーボックス番号変更履歴から取得した、ゲストキーボックスに現在設定されている番号。
 * m2m-cleaning `GET /v4/mypage/cleanings/:cleaningId/guest_keybox/latest` のレスポンス。
 */
export interface LatestGuestKeyboxNumberResponse {
    /** 本日時点で清掃員によるキーボックス番号変更の対象物件かどうか */
    isRotationTarget: boolean
    /** 変更履歴が無い場合は false（keyboxNumber は空文字） */
    isRegistered: boolean
    keyboxNumber: string
    rotatedAt: number | null
    /** 新しい順の変更履歴（最大5件）。histories を返さない古い API では undefined */
    histories?: GuestKeyboxNumberHistory[]
}

export interface GuestKeyboxNumberHistory {
    rotatedAt: number
    keyboxNumber: string
    /** BY_CLEANER: 清掃員による変更 / BY_MANAGER: 管理者による初期設定 / OTHER */
    type: string
}

/** 変更履歴の一覧に表示する 1 行 */
export interface KeyboxNumberHistoryRow {
    date: string
    keyboxNumber: string
}

// 端末のタイムゾーンに関係なく、JST の日付（YYYY-MM-DD）にする
const formatJstDate = (unixSeconds: number): string =>
    new Date((unixSeconds + 9 * 60 * 60) * 1000).toISOString().slice(0, 10)

/**
 * ゲストキー情報に表示する変更履歴を決める。
 * 番号を m2m-checkin から表示している（ローテ対象の）物件だけ表示し、
 * ローテ対象外の物件は物件運営情報の番号だけを表示する。
 */
export const resolveGuestKeyHistories = (
    latest: LatestGuestKeyboxNumberResponse | undefined
): KeyboxNumberHistoryRow[] => {
    if (!latest?.isRotationTarget) {
        return []
    }
    return (latest.histories ?? []).map(history => ({
        date: formatJstDate(history.rotatedAt),
        keyboxNumber: history.keyboxNumber
    }))
}

export const GUEST_KEY_NUMBER_UNREGISTERED =
    "番号未登録（キーボックスを開けられない場合は管理者に問い合わせてください）"

/**
 * ゲストキー情報に表示する番号を決める。
 *
 * ゲストキーがローテーションされる物件では、物件運営情報(BigQuery)の guest_key_number は
 * 「自動生成※m2mCheckinで確認できます。」のようなプレースホルダで現在の番号を表さないため、
 * m2m-checkin の最新番号を表示する。ローテーション対象外の物件（固定番号・TTLock など）は
 * BigQuery の値が実際の番号なので、従来どおりそれを表示する。
 * m2m-checkin から取得できなかった場合も、表示が消えないよう BigQuery の値を表示する。
 */
export const resolveGuestKeyNumber = (
    bigQueryGuestKeyNumber: string | undefined,
    latest: LatestGuestKeyboxNumberResponse | undefined
): string => {
    // 従来の表示（guest_key_number ?? "-"）と揃えるため、空文字はそのまま表示する
    const fallback = bigQueryGuestKeyNumber ?? "-"
    if (!latest?.isRotationTarget) {
        return fallback
    }
    if (latest.isRegistered && latest.keyboxNumber) {
        return latest.keyboxNumber
    }
    return GUEST_KEY_NUMBER_UNREGISTERED
}
